import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { Modal } from 'ant-design-vue';
import { useWatchfaceSave } from './use-watchface-save';
import { ShapeType } from '@/services/shape';

export function useShapeCount() {
  const editorStore = useEditorStore();
  const watchStore = useWatchfaceStore();

  // 通过theme获取shapeCount
  const _getShapeCountByTheme = (theme: StoreThemeItem) => {
    const _count = (layers: StoreLayerItem[]) => {
      let count = 0;

      layers.forEach((layer) => {
        if (layer.type == ShapeType.Widget) {
          count += _count(layer.children ?? []);
        } else if (layer.type == ShapeType.Slot) {
          const counts = layer.children?.map((widget) => {
            return _count(widget.children ?? []);
          }) ?? [0];
          count += Math.max(...counts);
        } else {
          count++;
        }
      });

      return count;
    };
    const count = _count(theme.children ?? []);
    return count;
  };

  const { editorDataToAst } = useWatchfaceSave();

  // 通过ast获取shapeCount
  const _getShapeCountByAst = async (theme: StoreThemeItem) => {
    const ast = await editorDataToAst({
      exportForApp: false,
      keepUslessAttr: true
    });
    if (!ast || !theme) return 0;

    const resourceAst = ast.children.find((item) => item.tag == 'Resources');

    // 保存已复用的ref
    const refs: string[] = [];
    const _ids: string[] = [];

    const _count = (layouts: Ast[]) => {
      let count = 0;
      layouts.forEach((layout) => {
        // 如果一个shape生成多个，则不计入数量
        const _id = layout.attrsMap._shapeId;
        if (_id) {
          if (_ids.includes(_id)) return;
          _ids.push(_id);
        }

        // 获取ref
        const ref = layout.attrsMap.ref;
        if (ref) {
          if (refs.includes(ref)) return;
          refs.push(ref);
        }

        // 获取ref对应的资源
        const refAst = resourceAst?.children.find(
          (item) => '@' + item.attrsMap.name == ref
        );
        if (!refAst) return;

        const tag = refAst?.tag;
        if (tag == 'Slot') {
          const counts = refAst.children?.map((slotLayout) => {
            const widgetAst = resourceAst?.children.find(
              (item) => '@' + item.attrsMap.name == slotLayout.attrsMap.ref
            );
            return _count(widgetAst?.children ?? []);
          }) ?? [0];
          count += Math.max(...counts);
        } else if (tag == 'Widget') {
          if (refAst?.attrsMap._asWidget) {
            count++;
          } else {
            count += _count(refAst.children ?? []);
          }
        } else {
          count++;
        }
      });
      return count;
    };

    // 依次计算每个theme下的图层数量
    const themeAst = ast.children.find(
      (item) =>
        item.tag == 'Theme' &&
        item.attrsMap.name == theme.name &&
        item.attrsMap.type == theme.type
    );
    const count = _count(themeAst?.children ?? []);
    return count;
  };

  /**
   * 验证当前样式的shapeCount
   * @returns
   */
  const validShapeCount = async () => {
    const theme = editorStore.theme;
    if (!theme) return true;

    const maxCount = watchStore.watchfaceDevice?.maxShapeCount ?? 300;
    let count = _getShapeCountByTheme(theme);

    if (count >= maxCount) {
      count = await _getShapeCountByAst(theme);
    }

    if (count >= maxCount) {
      Modal.destroyAll();
      Modal.warning({
        title: '无法添加',
        content: `最多支持${maxCount}个元素，已到达数量上限，无法添加！`,
        okText: '知道了'
      });
      return false;
    }
    return true;
  };

  return {
    validShapeCount
  };
}
